import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../../services/errorReporting.service';

interface Props {
  children: ReactNode;
  fallbackComponent?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

export interface ErrorFallbackProps {
  error?: Error;
  errorInfo?: ErrorInfo;
  resetError: () => void;
  errorId: string;
}

class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    this.logError(error, errorInfo);
    
    // Update state with error info
    this.setState({
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetOnPropsChange, children } = this.props;
    const { hasError } = this.state;

    // Reset error state if props change and resetOnPropsChange is true
    if (hasError && resetOnPropsChange && prevProps.children !== children) {
      this.resetError();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private logError = (error: Error, errorInfo: ErrorInfo) => {
    // Use the centralized error reporting service
    const errorId = reportError(error, {
      componentStack: errorInfo.componentStack,
      userId: this.getCurrentUserId(),
      severity: 'high', // Error boundaries catch high-severity errors
      additionalContext: {
        boundaryName: this.constructor.name,
        isolate: this.props.isolate || false,
        hasCustomFallback: !!this.props.fallbackComponent
      }
    });

    // Update state with the error ID from the service
    this.setState({ errorId });
  };

  private getCurrentUserId = (): string | null => {
    try {
      // Try to get current user from Firebase Auth
      const auth = require('firebase/auth').getAuth();
      return auth.currentUser?.uid || null;
    } catch {
      return null;
    }
  };


  private resetError = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: ''
    });
  };

  private handleRetry = () => {
    this.resetError();
    
    // Optional: reload the page after a short delay if the error persists
    this.resetTimeoutId = window.setTimeout(() => {
      if (this.state.hasError) {
        window.location.reload();
      }
    }, 5000);
  };

  render() {
    const { hasError, error, errorInfo, errorId } = this.state;
    const { children, fallbackComponent: FallbackComponent, isolate } = this.props;

    if (hasError) {
      // Use custom fallback component if provided
      if (FallbackComponent) {
        return (
          <FallbackComponent
            error={error}
            errorInfo={errorInfo}
            resetError={this.handleRetry}
            errorId={errorId}
          />
        );
      }

      // Default fallback UI
      return (
        <DefaultErrorFallback
          error={error}
          errorInfo={errorInfo}
          resetError={this.handleRetry}
          errorId={errorId}
          isolate={isolate}
        />
      );
    }

    return children;
  }
}

// Default fallback component
const DefaultErrorFallback: React.FC<ErrorFallbackProps & { isolate?: boolean }> = ({
  error,
  errorInfo,
  resetError,
  errorId,
  isolate = false
}) => {
  const isDevelopment = process.env.NODE_ENV === 'development';

  const containerStyle: React.CSSProperties = {
    padding: isolate ? '1rem' : '2rem',
    margin: isolate ? '0.5rem 0' : '1rem',
    border: '2px solid var(--color-accent)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-background-secondary)',
    color: '#ffffff',
    textAlign: 'center',
    minHeight: isolate ? 'auto' : '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isolate ? '1.2rem' : '1.5rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: 'var(--color-accent)'
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '1rem',
    marginBottom: '1.5rem',
    lineHeight: 1.5
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'var(--color-accent)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--border-radius-sm)',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'var(--transition-fast)',
    marginRight: '1rem'
  };

  const detailsStyle: React.CSSProperties = {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 'var(--border-radius-sm)',
    textAlign: 'left',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '200px'
  };

  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>
        {isolate ? '⚠️ Component Error' : '🚨 Something went wrong'}
      </h2>
      
      <p style={messageStyle}>
        {isolate 
          ? 'This component encountered an error, but the rest of the page should work normally.'
          : 'We encountered an unexpected error. Please try again or refresh the page.'
        }
      </p>

      <div>
        <button 
          style={buttonStyle}
          onClick={resetError}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          Try Again
        </button>
        
        <button 
          style={{...buttonStyle, backgroundColor: 'transparent', border: '1px solid var(--color-accent)'}}
          onClick={() => window.location.reload()}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          Refresh Page
        </button>
      </div>

      {isDevelopment && error && (
        <details style={detailsStyle}>
          <summary style={{ cursor: 'pointer', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Error Details (Development Only)
          </summary>
          <div>
            <strong>Error:</strong> {error.message}
            {error.stack && (
              <div>
                <strong>Stack:</strong>
                <pre>{error.stack}</pre>
              </div>
            )}
            {errorInfo?.componentStack && (
              <div>
                <strong>Component Stack:</strong>
                <pre>{errorInfo.componentStack}</pre>
              </div>
            )}
            <div>
              <strong>Error ID:</strong> {errorId}
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

export default ErrorBoundary;